import { all, call, delay, fork, put, select, takeEvery } from 'redux-saga/effects';
import { fetchResource, updateEntity } from 'helpers/utilHelpers';
import {
  CHANGE_CREDENTIAL_CATEGORY,
  CHANGE_CREDENTIAL_FILTER,
  CHANGE_PROJECT_CREDENTIALS_CATEGORY,
  CHANGE_PROJECT_CREDENTIALS_FILTER,
  CHANGE_PROJECT_TASKS_FILTER,
  CHANGE_PROJECT_TASKS_LABEL_ID,
  LOG_APP_ERROR,
  SAVE_SETTING,
  SHOW_APPLAUSE,
  TOGGLE_APP_ERROR,
} from 'constants/ActionTypes';

import { CREDENTIAL_LIST, PROJECT_CREDENTIAL_LIST, PROJECT_TASK_LIST } from 'constants/SettingConstants';

import { hideApplause } from 'actions/settingActions';

import { getCredentialListFilters } from 'selectors/ui/credentialListSelectors';
import { getProjectCredentialsFilters, getProjectTasksFilters } from 'selectors/ui/projectViewSelectors';
import { getSortingByKey } from 'selectors/settingsSelectors';
import { authUserIdSelector } from 'selectors/authSelectors';

const RESOURCE = 'state';
const METHOD = 'PUT';

function* fetchCredentialList(action) {
  let values = yield select(getCredentialListFilters);

  if (action.type === CHANGE_CREDENTIAL_FILTER) {
    values = values.set('filter', action.filter);
  }

  if (action.type === CHANGE_CREDENTIAL_CATEGORY) {
    values = values.set('category', action.category);
  }

  try {
    yield call(() =>
      fetchResource(RESOURCE, METHOD, {
        key: CREDENTIAL_LIST,
        value: {
          filters: values.toJS(),
        },
      }),
    );
  } catch (response) {
    //TODO: Add scenario error.
  }
}

function* fetchProjectCredentialList(action) {
  let values = yield select(getProjectCredentialsFilters);

  if (action.type === CHANGE_PROJECT_CREDENTIALS_FILTER) {
    values = values.set('filter', action.filter);
    if (action.filter === 'all') {
      values = values.set('projectId', null);
    }
  }

  if (action.type === CHANGE_PROJECT_CREDENTIALS_CATEGORY) {
    values = values.set('category', action.category);
  }

  try {
    yield call(() =>
      fetchResource(RESOURCE, METHOD, {
        key: PROJECT_CREDENTIAL_LIST,
        value: {
          filters: values.toJS(),
        },
      }),
    );
  } catch (response) {
    //TODO: Add scenario error.
  }
}

function* fetchProjectTaskList(action) {
  let values = yield select(getProjectTasksFilters);
  let sorting = yield select((state) => getSortingByKey(state, { settingKey: PROJECT_TASK_LIST }));

  if (action.type === CHANGE_PROJECT_TASKS_FILTER) {
    values = values.set('filter', action.filter);
  }

  if (action.type === CHANGE_PROJECT_TASKS_LABEL_ID) {
    values = values.set('labelId', action.labelId);
  }

  try {
    yield call(() =>
      fetchResource(RESOURCE, METHOD, {
        key: PROJECT_TASK_LIST,
        value: {
          filters: values.toJS(),
          sorting,
        },
      }),
    );
  } catch (response) {
    //TODO: Add scenario error.
  }
}

function* fetchAppError(action) {
  let values = null;
  if (action.type === LOG_APP_ERROR) {
    values = action.values;
  } else {
    const userId = yield select(authUserIdSelector);
    values = {
      user: userId,
      message: action.errorCode,
      traceLog: action.errorContent,
    };
  }

  // Send error report
  try {
    if (process.env.NODE_ENV === 'production' && ![404, 403].includes(action.errorCode)) {
      yield call(() => fetchResource('email-logs', 'POST', values));
    }
  } catch (response) {
    // At least we tried...
  }
}

function* showApplause() {
  yield delay(3 * 1000);
  yield put(hideApplause());
}

function* saveState() {
  yield takeEvery(SAVE_SETTING, updateEntity);
}

function* saveCredentialList() {
  yield takeEvery([CHANGE_CREDENTIAL_FILTER, CHANGE_CREDENTIAL_CATEGORY], fetchCredentialList);
}

function* saveProjectCredentialList() {
  yield takeEvery([CHANGE_PROJECT_CREDENTIALS_FILTER, CHANGE_PROJECT_CREDENTIALS_CATEGORY], fetchProjectCredentialList);
}

function* saveProjectTaskList() {
  yield takeEvery([CHANGE_PROJECT_TASKS_FILTER, CHANGE_PROJECT_TASKS_LABEL_ID], fetchProjectTaskList);
}

function* animationApplause() {
  yield takeEvery(SHOW_APPLAUSE, showApplause);
}

function* logAppError() {
  yield takeEvery(LOG_APP_ERROR, fetchAppError);
}

function* watchError() {
  yield takeEvery(TOGGLE_APP_ERROR, fetchAppError);
}

export default function* rootSaga() {
  yield all([
    fork(saveState),
    fork(saveCredentialList),
    fork(saveProjectCredentialList),
    fork(saveProjectTaskList),
    fork(animationApplause),
    fork(logAppError),
    fork(watchError),
  ]);
}
